<script setup>
import { computed, onBeforeUnmount, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { isWhatsAppComplete } from '@chatwoot/utils';
import Input from 'dashboard/components-next/input/Input.vue';
import {
  MEDIA_FORMATS,
  buildTemplateParameters,
  findComponentByType,
  renderTemplatePreview,
} from 'dashboard/helper/templateHelper';

const props = defineProps({
  template: { type: Object, required: true },
  savedParams: { type: Object, default: null },
  previewUrl: { type: String, default: '' },
});

const MEDIA_ACCEPT = {
  IMAGE: 'image/jpeg,image/png',
  VIDEO: 'video/mp4',
  DOCUMENT: 'application/pdf',
};
const MEDIA_MAX_MB = { IMAGE: 5, VIDEO: 16, DOCUMENT: 100 };
const NAME_LIMIT = 25;

const { t } = useI18n();
const processedParams = ref({});
const headerFile = ref(null);
const headerFileName = ref('');
const fileError = ref('');
const localPreviewUrl = ref('');
const fileInput = ref(null);

const header = computed(() => findComponentByType(props.template, 'HEADER'));
const headerFormat = computed(() => header.value?.format || '');
const hasMediaHeader = computed(() =>
  MEDIA_FORMATS.includes(headerFormat.value)
);
const bodyText = computed(
  () => findComponentByType(props.template, 'BODY')?.text || ''
);
const headerText = computed(() =>
  headerFormat.value === 'TEXT' ? header.value?.text || '' : ''
);
const renderedBody = computed(() =>
  renderTemplatePreview(bodyText.value, processedParams.value.body || {})
);
const renderedHeader = computed(() =>
  renderTemplatePreview(headerText.value, processedParams.value.header || {})
);
const footerText = computed(
  () => findComponentByType(props.template, 'FOOTER')?.text || ''
);
const hasFields = computed(
  () =>
    hasMediaHeader.value ||
    Boolean(processedParams.value.body) ||
    Boolean(processedParams.value.header && headerFormat.value === 'TEXT') ||
    Boolean(processedParams.value.buttons)
);
const imagePreview = computed(
  () => localPreviewUrl.value || props.previewUrl || ''
);
const mediaLabel = computed(() => {
  if (headerFormat.value === 'VIDEO') {
    return t('INBOX_MGMT.RECONTACT.FORM.MEDIA_VIDEO');
  }
  if (headerFormat.value === 'DOCUMENT') {
    return t('INBOX_MGMT.RECONTACT.FORM.MEDIA_DOCUMENT');
  }
  return t('INBOX_MGMT.RECONTACT.FORM.MEDIA_IMAGE');
});
const textHeaderKeys = computed(() =>
  Object.keys(processedParams.value.header || {}).filter(
    key => !key.startsWith('media_')
  )
);
const hasStoredMedia = computed(() =>
  Boolean(
    props.savedParams?.header?.media_key || props.savedParams?.header?.media_url
  )
);
const paramsForCheck = computed(() => {
  if (!headerFile.value && !hasStoredMedia.value) return processedParams.value;
  return {
    ...processedParams.value,
    header: {
      ...processedParams.value.header,
      media_url: 'https://files.invalid/selected',
    },
  };
});
const isFormInvalid = computed(
  () =>
    Boolean(fileError.value) ||
    !isWhatsAppComplete(props.template, paramsForCheck.value)
);

const resetFile = () => {
  if (localPreviewUrl.value) URL.revokeObjectURL(localPreviewUrl.value);
  localPreviewUrl.value = '';
  headerFile.value = null;
  headerFileName.value = '';
  fileError.value = '';
  if (fileInput.value) fileInput.value.value = '';
};

const loadTemplate = () => {
  const built = buildTemplateParameters(props.template);
  const saved = props.savedParams || {};
  processedParams.value = {
    ...built,
    ...saved,
    body: { ...built.body, ...saved.body },
    header: { ...built.header, ...saved.header },
  };
  if (!saved.buttons && built.buttons) {
    processedParams.value.buttons = built.buttons;
  }
  resetFile();
  if (saved.header?.media_name) headerFileName.value = saved.header.media_name;
};

const onFile = event => {
  fileError.value = '';
  const file = event.target.files?.[0];
  if (!file) {
    resetFile();
    return;
  }
  const base = file.name.replace(/\.[^.]+$/, '');
  if (base.length > NAME_LIMIT || base.length === 0) {
    fileError.value = t('INBOX_MGMT.RECONTACT.FORM.MEDIA_FILENAME');
    event.target.value = '';
    return;
  }
  const maxMb = MEDIA_MAX_MB[headerFormat.value] || 16;
  if (file.size > maxMb * 1024 * 1024) {
    fileError.value = t('INBOX_MGMT.RECONTACT.FORM.MEDIA_TOO_LARGE', {
      max: maxMb,
    });
    event.target.value = '';
    return;
  }
  if (localPreviewUrl.value) URL.revokeObjectURL(localPreviewUrl.value);
  headerFile.value = file;
  headerFileName.value = file.name;
  localPreviewUrl.value = file.type.startsWith('image/')
    ? URL.createObjectURL(file)
    : '';
};

watch(() => props.template, loadTemplate, { immediate: true });
onBeforeUnmount(resetFile);

defineExpose({ processedParams, isFormInvalid, headerFile });
</script>

<template>
  <div class="grid items-start gap-4 lg:grid-cols-2">
    <div v-if="hasFields" class="flex flex-col gap-4">
      <div v-if="hasMediaHeader" class="flex flex-col gap-2">
        <p class="mb-0 text-sm font-medium text-n-slate-12">
          {{ t('INBOX_MGMT.RECONTACT.FORM.MEDIA') }}
        </p>
        <label
          class="flex cursor-pointer items-center justify-center gap-2 rounded-lg border border-dashed border-n-slate-7 bg-n-alpha-3 px-4 py-3 text-sm text-n-slate-12 hover:bg-n-alpha-2"
        >
          <span class="i-lucide-upload size-4" />
          {{ headerFileName || mediaLabel }}
          <input
            ref="fileInput"
            type="file"
            class="sr-only"
            :accept="MEDIA_ACCEPT[headerFormat]"
            @change="onFile"
          />
        </label>
        <p v-if="fileError" class="mb-0 text-sm text-n-ruby-9">
          {{ fileError }}
        </p>
      </div>
      <div
        v-if="processedParams.header && headerFormat === 'TEXT'"
        class="flex flex-col gap-2"
      >
        <p class="mb-0 text-sm font-medium text-n-slate-12">
          {{ t('WHATSAPP_TEMPLATES.PARSER.HEADER_VARIABLES_LABEL') }}
        </p>
        <Input
          v-for="key in textHeaderKeys"
          :key="`header-${key}`"
          v-model="processedParams.header[key]"
          type="text"
          :placeholder="
            t('WHATSAPP_TEMPLATES.PARSER.VARIABLE_PLACEHOLDER', {
              variable: key,
            })
          "
        />
      </div>
      <div v-if="processedParams.body" class="flex flex-col gap-2">
        <p class="mb-0 text-sm font-medium text-n-slate-12">
          {{ t('WHATSAPP_TEMPLATES.PARSER.VARIABLES_LABEL') }}
        </p>
        <Input
          v-for="(_value, key) in processedParams.body"
          :key="`body-${key}`"
          v-model="processedParams.body[key]"
          type="text"
          :placeholder="
            t('WHATSAPP_TEMPLATES.PARSER.VARIABLE_PLACEHOLDER', {
              variable: key,
            })
          "
        />
      </div>
      <div v-if="processedParams.buttons" class="flex flex-col gap-2">
        <p class="mb-0 text-sm font-medium text-n-slate-12">
          {{ t('WHATSAPP_TEMPLATES.PARSER.BUTTON_PARAMETERS') }}
        </p>
        <template
          v-for="(button, index) in processedParams.buttons"
          :key="`button-${index}`"
        >
          <Input
            v-if="button"
            v-model="processedParams.buttons[index].parameter"
            type="text"
            :placeholder="t('WHATSAPP_TEMPLATES.PARSER.BUTTON_PARAMETER')"
          />
        </template>
      </div>
    </div>
    <aside
      class="rounded-xl bg-n-alpha-black2 p-4 lg:sticky lg:top-4"
      :class="{ 'lg:col-start-2': !hasFields }"
    >
      <p class="mb-3 text-sm font-medium text-n-slate-12">
        {{ t('INBOX_MGMT.RECONTACT.FORM.PREVIEW') }}
      </p>
      <img
        v-if="hasMediaHeader && headerFormat === 'IMAGE' && imagePreview"
        :src="imagePreview"
        alt=""
        class="mb-3 max-h-40 w-full rounded-lg object-cover"
      />
      <p
        v-else-if="hasMediaHeader && headerFileName"
        class="mb-3 text-sm text-n-slate-11"
      >
        {{ headerFileName }}
      </p>
      <p
        v-if="renderedHeader"
        class="mb-2 whitespace-pre-wrap text-sm font-medium text-n-slate-12"
      >
        {{ renderedHeader }}
      </p>
      <p class="mb-0 whitespace-pre-wrap text-sm text-n-slate-12">
        {{ renderedBody }}
      </p>
      <p v-if="footerText" class="mb-0 mt-3 text-xs text-n-slate-11">
        {{ footerText }}
      </p>
    </aside>
  </div>
</template>
